import { Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { formatEther, formatUnits } from 'ethers/lib/utils';
import { useEffect, useState } from 'react';
import { fetchBlockNumber } from 'wagmi/actions';

import earnedRewardIcon from '../../common/svg/dashboard/earned-reward-icon.svg';
import projectedRewardIcon from '../../common/svg/dashboard/projected-reward-icon.svg';
import stakingEntriesIcon from '../../common/svg/dashboard/staking-entries-icon.svg';
import totalBalanceIcon from '../../common/svg/dashboard/total-balance-icon.svg';
import { useWeb3State } from '../../hooks';
import { useStarsEthLpTokenContract } from '../../hooks/lp-token-contract.hooks';
import { useStarStakingContract } from '../../hooks/stars-staking.hook';
import { WEB3_CONFIG } from '../../lib';
import styles from './DashboardStakingStats.module.css';

export const DashboardStakingStats = () => {
  const [os, setOs] = useState('WindowsLike');
  const [portfolioSum, setPortfolioSum] = useState({
    projectedRewards: 0,
    earnedRewards: 0,
    balance: 0,
    entries: 0,
  });
  const [intervalStarted, setIntervalStarted] = useState(false);
  const [loading, setLoading] = useState(true);

  const { getStarsEthTotalSupply, getStarsEthReserves, initialized } =
    useStarsEthLpTokenContract(true);

  const {
    getDeposit,
    getPool,
    getPoolLength,
    depositLength,
    contractInitialized,
    pendingSTARS,
  } = useStarStakingContract(true);

  const { walletDetails, isInitialized } = useWeb3State();

  useEffect(() => {
    if (
      navigator.userAgent.includes('Mac') ||
      navigator.userAgent.includes('like Mac')
    ) {
      setOs('MacintoshLike');
    }
  });

  useEffect(() => {
    const setPortfolioSummary = async () => {
      const [currentBlock, poolLength] = await Promise.all([
        fetchBlockNumber({ chainId: WEB3_CONFIG.chains.bsc.id }),
        getPoolLength(),
      ]);

      let projectedRewards = 0;
      let earnedRewards = 0;
      let balance = 0;
      let entries = 0;

      for (let x = 0; x < poolLength.poolLength; x++) {
        const pool = (await getPool(x)).pool;
        const depLength = (
          await depositLength(x, walletDetails.address ?? '')
        ).length?.toNumber();
        if (pool !== undefined) {
          const startBlock = pool[4].toNumber();
          const endBlock = pool[5].toNumber();
          const starsPerBlock = parseFloat(formatUnits(pool[6], 18));
          const totalDepositedLP = parseFloat(formatUnits(pool[7], 18));
          if (depLength !== undefined) {
            for (let y = 0; y < depLength; y++) {
              const deposit = (
                await getDeposit(x, walletDetails.address ?? '', y)
              ).deposit;
              let depositAmount = 0;
              if (deposit !== undefined) {
                depositAmount = parseFloat(formatUnits(deposit[2], 18));
                entries += 1;
                if (x === 2) {
                  const currentLpTotalSupply = parseFloat(
                    formatEther(await getStarsEthTotalSupply()),
                  );

                  const currentBnbStarsReserves = await getStarsEthReserves();

                  const lpRatio =
                    parseFloat(formatUnits(pool[7], 18)) / currentLpTotalSupply;
                  const lpToStars =
                    parseFloat(formatEther(currentBnbStarsReserves[1])) *
                    lpRatio;
                  balance += lpToStars;
                } else {
                  balance += depositAmount;
                }

                const pendingRewards = (
                  await pendingSTARS(x, walletDetails.address ?? '', y)
                ).pendingStars;

                if (pendingRewards !== undefined) {
                  earnedRewards += parseFloat(formatEther(pendingRewards));
                }

                const poolSharePercentage = depositAmount / totalDepositedLP;
                if (currentBlock <= endBlock) {
                  projectedRewards +=
                    (endBlock - Math.max(Number(currentBlock), startBlock)) *
                    starsPerBlock *
                    poolSharePercentage;
                }
              }
            }
          }
        }
      }
      setLoading(false);
      setPortfolioSum({ projectedRewards, earnedRewards, balance, entries });
    };

    if (contractInitialized && isInitialized && initialized) {
      setIntervalStarted(true);
      void setPortfolioSummary();

      if (!intervalStarted) {
        const interval = setInterval(() => {
          void setPortfolioSummary();
        }, 5000);
        return () => clearInterval(interval);
      }
    }
  }, [contractInitialized, isInitialized, initialized]);

  return (
    <div
      className={
        styles.stakingStatsContainer +
        ' w-full flex lg:flex-nowrap flex-wrap gap-5'
      }
    >
      <div
        className={
          styles.stakingCard +
          ' lg:w-7/12 w-full flex justify-center items-center gap-4 relative lg:py-10 py-4'
        }
      >
        <Tooltip
          title={
            'Potential rewards from now till the end of the staking period based on your deposits'
          }
          placement="top"
        >
          {os === 'WindowsLike' ? (
            <div
              className={
                styles.toolTip +
                ' flex justify-center items-center absolute top-3 right-3 text-sm box-border pointer'
              }
            >
              ?
            </div>
          ) : (
            <div
              className={
                styles.toolTipMac +
                ' flex justify-center items-center absolute top-3 right-3 text-sm box-border pointer'
              }
            >
              ?
            </div>
          )}
        </Tooltip>
        <img
          className="w-16 lg:w-24 xl:w-auto"
          src={projectedRewardIcon}
          alt="projectedReward"
        />
        <div className="flex flex-col justify-center lg:gap-4">
          <div className={'xl:text-4xl lg:text-3xl text-2xl'}>
            PROJECTED REWARDS
          </div>
          {loading ? (
            <Skeleton
              variant="rect"
              height={35}
              width={220}
              style={{ backgroundColor: '#383D4B' }}
            />
          ) : (
            <div
              className={
                styles.starsAmount + ' xl:text-5xl lg:text-4xl text-3xl'
              }
            >
              {portfolioSum.projectedRewards.toFixed(2)} MOGUL
            </div>
          )}
        </div>
      </div>
      <div className="lg:w-5/12 w-full flex flex-col justify-center items-center gap-5">
        <div
          className={
            styles.stakingCard +
            ' w-full h-24 flex justify-center items-center xl:gap-6 lg:gap-3 gap-4 pt-1 box-border'
          }
        >
          <img
            className="lg:w-14 xl:w-auto -mr-2 sm:mr-0"
            src={earnedRewardIcon}
            alt="earnedReward"
          />
          <div className="flex flex-col justify-center">
            <div className={'xl:text-2xl lg:text-xl text-2xl'}>
              TOTAL REWARDS EARNED
            </div>
            {loading ? (
              <Skeleton
                variant="rect"
                height={30}
                width={220}
                style={{ backgroundColor: '#383D4B' }}
              />
            ) : (
              <div
                className={
                  styles.starsAmount + ' xl:text-2xl lg:text-xl text-3xl'
                }
              >
                {portfolioSum.earnedRewards.toFixed(2)} MOGUL
              </div>
            )}
          </div>
        </div>
        <div className="w-full flex gap-5">
          <div
            className={
              styles.stakingCard +
              ' w-8/12 h-20 flex justify-center items-center gap-3'
            }
          >
            <img
              className="lg:w-7 xl:w-auto"
              src={totalBalanceIcon}
              alt="totalBalance"
            />
            <div className="flex flex-col justify-center mt-1">
              <div className={'xl:text-xl lg:text-base'}>TOTAL BALANCE</div>
              {loading ? (
                <Skeleton
                  variant="rect"
                  height={15}
                  width={100}
                  style={{ backgroundColor: '#383D4B' }}
                />
              ) : (
                <div className={styles.starsAmount + ' text-xl lg:text-base'}>
                  {portfolioSum.balance.toFixed(2)} MOGUL
                </div>
              )}
            </div>
          </div>
          <div
            className={
              styles.stakingCard +
              ' w-4/12 h-20 flex justify-center items-center xl:gap-3 gap-2 relative'
            }
          >
            <Tooltip
              title={'Total number of deposits you made'}
              placement="top"
            >
              {os === 'WindowsLike' ? (
                <div
                  className={
                    styles.toolTipSmall +
                    ' flex justify-center items-center absolute top-2 right-2 box-border pointer'
                  }
                >
                  ?
                </div>
              ) : (
                <div
                  className={
                    styles.toolTipSmallMac +
                    ' flex justify-center items-center absolute top-2 right-2 box-border pointer'
                  }
                >
                  ?
                </div>
              )}
            </Tooltip>
            <img
              className="lg:w-7 xl:w-auto"
              src={stakingEntriesIcon}
              alt="stakingEntries"
            />
            <div className="flex flex-col justify-center mt-2">
              <div className={'text-xs'}>STAKING</div>
              <div className={'text-xs -mt-1'}>ENTRIES</div>
              {loading ? (
                <Skeleton
                  variant="rect"
                  height={15}
                  width={40}
                  style={{ backgroundColor: '#383D4B' }}
                />
              ) : (
                <div className={styles.starsAmount + ' text-xl'}>
                  {portfolioSum.entries}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
